import { useMemo } from 'react';

import { type QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';

import { useSwallowedMutation } from '../_useSwallowedMutation.js';
import { invalidateMutationKey, retryServerErrorsOnly } from '../_utils.js';
import { useApiClient } from '../useApiClient.js';

export const NOTIFICATIONS_QUERY_KEY = 'notifications';

export const NOTIFICATIONS_POLL_INTERVAL_MS = 30_000;

export type NotificationOutcome = 'success' | 'failure';

export type NotificationResourceType = 'account' | 'generic_order';

export type Notification = {
  id: string;
  createdAt?: string;
  message?: string;
  notificationType?: string;
  outcome?: NotificationOutcome;
  readAt?: string | null;
  resourceId?: number | null;
  resourceLabel?: string | null;
  resourceType?: NotificationResourceType | null;
  title?: string;
};

export type NotificationCounts = {
  total: number;
  unread: number;
};

export type NotificationsFilters = {
  isRead?: boolean;
};

export type MarkNotificationAsReadVariables = {
  notificationId: string | number;
};

type UseNotificationsParams = {
  enabled?: boolean;
  mutationKey?: QueryKey | QueryKey[];
  onError?: (error: unknown) => void;
};

const EMPTY_COUNTS: NotificationCounts = { total: 0, unread: 0 };

export const useNotifications = ({
  enabled = true,
  mutationKey,
  onError,
}: UseNotificationsParams = {}) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const {
    data: countsData,
    isFetching,
    refetch: refetchCounts,
  } = useQuery({
    queryKey: [NOTIFICATIONS_QUERY_KEY, 'counts'],
    queryFn: async () => {
      const response = await apiClient.notifications.countsNotificationsNotificationsCounts();
      return response.data;
    },
    enabled,
    refetchInterval: NOTIFICATIONS_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    retry: retryServerErrorsOnly,
  });

  const counts = useMemo<NotificationCounts>(
    () => ({
      total: countsData?.total ?? EMPTY_COUNTS.total,
      unread: countsData?.unread ?? EMPTY_COUNTS.unread,
    }),
    [countsData]
  );

  const { mutateAsync: markAsRead, isPending: isMarkingAsRead } = useSwallowedMutation({
    mutationFn: async ({ notificationId }: MarkNotificationAsReadVariables) => {
      const response =
        await apiClient.notifications.markAsReadNotificationsNotificationsIdMarkAsRead({
          id: String(notificationId),
        });
      return response.data;
    },
    onSuccess: () => invalidateMutationKey(queryClient, mutationKey),
    onError,
  });

  const { mutateAsync: markAllAsRead, isPending: isMarkingAllAsRead } = useSwallowedMutation({
    mutationFn: async () => {
      const response =
        await apiClient.notifications.markAllAsReadNotificationsNotificationsMarkAllAsRead();
      return response.data;
    },
    onSuccess: () => invalidateMutationKey(queryClient, mutationKey),
    onError,
  });

  const isPending = isMarkingAsRead || isMarkingAllAsRead;

  return useMemo(
    () => ({
      counts,
      isFetching,
      isPending,
      markAllAsRead,
      markAsRead,
      refetchCounts,
    }),
    [counts, isFetching, isPending, markAllAsRead, markAsRead, refetchCounts]
  );
};
